import React, { useEffect, useState } from "react";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { Row, Col, Button, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FormInput from "../../../components/FormInput";
import { getBankById, updateBank } from "../../../middleware/backend_hepler";

const EditBankForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const methods = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      bank_name: "",
      selected_loan_types: [],
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = methods;

  const loanTypeOptions = [
    { value: "personal_loan", label: "Personal Loan" },
    { value: "business_loan", label: "Business Loan" },
    { value: "doctor_loan", label: "Doctor Loan" },
    { value: "home_loan", label: "Home Loan" },
    { value: "mortgage_loan", label: "Mortgage Loan" },
    { value: "nri_loan", label: "NRI Loan" },
  ];

  useEffect(() => {
    const fetchBankData = async () => {
      if (id) {
        try {
          const response = await getBankById(id);
          const bankData = response.data;

          const selectedLoanTypes = loanTypeOptions
            .filter((option) => bankData[option.value] === 1)
            .map((option) => option.value);

          setValue("bank_name", bankData.bank_name || "");
          setValue("selected_loan_types", selectedLoanTypes);
        } catch (error) {
          console.error("Error fetching bank:", error);
          setError(error);
        }
      }
    };

    fetchBankData();
  }, [id, setValue]);

  const onSubmit = async (data) => {
    setLoading(true);
    const selectedLoanTypes = data.selected_loan_types || [];

    const loanTypeStatus = {
      home_loan: selectedLoanTypes.includes("home_loan") ? 1 : 0,
      personal_loan: selectedLoanTypes.includes("personal_loan") ? 1 : 0,
      business_loan: selectedLoanTypes.includes("business_loan") ? 1 : 0,
      mortgage_loan: selectedLoanTypes.includes("mortgage_loan") ? 1 : 0,
      doctor_loan: selectedLoanTypes.includes("doctor_loan") ? 1 : 0,
      nri_loan: selectedLoanTypes.includes("nri_loan") ? 1 : 0,
    };

    const updatedData = {
      bank_name: data.bank_name.trim(),
      ...loanTypeStatus,
    };

    try {
      await updateBank(id, updatedData);
      navigate("/banklist", {
        state: { successMessage: "Bank updated successfully!" },
      });
    } catch (error) {
      const errorMessage = error.response?.data?.message;
      console.error("Update bank failed:", errorMessage);

      if (error.response?.status === 409 || errorMessage?.includes("Duplicate")) {
        toast.error("Bank with this name already exists.");
      } else {
        toast.error("Failed to update bank. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate("/banklist");
  };

  return (
    <div className="container">
      <div className="edit-back-icon">
        <div className="edit-back-text" onClick={handleGoBack}>
          &lt; Go Back
        </div>
      </div>
      <h2 className="my-4">Bank</h2>
      <FormProvider {...methods}>
        <div className="form-content">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="sections personal-details-sections">
              <h4>Personal Details</h4>
              <Row>
                <Col md={6}>
                  <FormInput
                    name="bank_name"
                    label="Bank Name"
                    registerOptions={{
                      required: "Bank Name is required",
                    }}
                    error={errors.bank_name?.message}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <label className="form-label">Loan Types</label>
                  <Controller
                    name="selected_loan_types"
                    control={control}
                    rules={{
                      validate: (value) =>
                        value && value.length > 0
                          ? true
                          : "At least one loan type is required",
                    }}
                    render={({ field }) => (
                      <>
                        {loanTypeOptions.map((option) => (
                          <Form.Check
                            key={option.value}
                            type="checkbox"
                            id={option.value}
                            label={option.label}
                            value={option.value}
                            checked={field.value.includes(option.value)}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              const updatedValues = isChecked
                                ? [...field.value, option.value]
                                : field.value.filter((v) => v !== option.value);
                              field.onChange(updatedValues);
                            }}
                            className="mb-2"
                          />
                        ))}
                      </>
                    )}
                  />
                  {errors.selected_loan_types && (
                    <span className="text-danger">
                      {errors.selected_loan_types.message}
                    </span>
                  )}
                </Col>
              </Row>

              <Row className="mt-4">
                <Col className="text-end">
                  <Button
                    type="submit"
                    variant="primary"
                    className="custom-submit-button"
                    disabled={loading}
                  >
                    {loading ? "Updating..." : "Update"}
                  </Button>
                </Col>
              </Row>
            </div>
          </form>
        </div>
      </FormProvider>
      <ToastContainer />
    </div>
  );
};

export default EditBankForm;
